import { useMeeting } from "@videosdk.live/react-sdk";
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Chat,
  Channel,
  ChannelHeader,
  MessageInput,
  MessageList,
  Thread,
  Window,
  TypingIndicator,
  useChannelStateContext,
  ChannelList,
} from "stream-chat-react";
import {
  initGetStreamChat,
  disconnectGetStreamChat,
  fetchChannelMessagesForExport,
  formatMessagesAsTranscript,
} from "../../utils/getStreamClient";

import "stream-chat-react/dist/css/v2/index.css";
import "./ChatPanel.css";
import chatBg from "../../images/branding/chat-background.png";
import CustomMessage from "./CustomMessage";
import ModerationActions from "./ModerationActions";
import { useSiteContext } from "../../context/SiteContext";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import useIsMobile from "../../hooks/useIsMobile";
import useWindowSize from "../../hooks/useWindowSize";
import { toast } from "react-toastify";
import { useBreakoutRooms } from "../../context/BreakoutRoomsContext";

// Helper function to get user initials
function getUserInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
}

export function ChatPanel({ panelHeight }) {
  const mMeeting = useMeeting();
  const { isInSameRoom, currentRoom, participantRooms, rooms } = useBreakoutRooms();
  const [chatClient, setChatClient] = useState(null);
  const [channel, setChannel] = useState(null);
  const [activeChannel, setActiveChannel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModerator, setIsModerator] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState("everyone");
  const [privateChannels, setPrivateChannels] = useState(new Map());
  const [privateFeed, setPrivateFeed] = useState([]);
  const subscribedPrivateChannelsRef = useRef(new Set());
  const [messageVersion, setMessageVersion] = useState(0);
  const chatMainRef = useRef(null);
  const { meetingSession, meetingSettings, customMeetingName } =
    useSiteContext();
  const { setUnreadChat } = useMeetingAppContext();
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [openPinnedMenuId, setOpenPinnedMenuId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const isMobile = useIsMobile();
  const windowSize = useWindowSize();
  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;
  const isMobileLandscape = isMobile && isLandscapeOrientation;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openPinnedMenuId && !event.target.closest(".pinned-menu-wrapper")) {
        setOpenPinnedMenuId(null);
      }
    };

    if (openPinnedMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openPinnedMenuId]);

  const updateLocalPinnedState = useCallback(
    (msg, pinned, ch) => {
      if (!msg || !ch) return;
      try {
        const newMessage = {
          ...msg,
          pinned,
          pinned_at: pinned ? msg?.pinned_at || new Date().toISOString() : null,
          pinned_by: pinned ? msg?.pinned_by || chatClient?.user : null,
        };
        ch.state.updateMessage(newMessage);
      } catch (e) {
        // ignore
      }
    },
    [chatClient?.user]
  );
  const markChannelRead = useCallback(async () => {
    try {
      const ch = activeChannel || channel;
      if (!ch) return;
      ch.lastReadLocal = new Date().toISOString();
      setMessageVersion((v) => v + 1);
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("chat-mark-read", {
              detail: { cid: ch.cid, ts: ch.lastReadLocal },
            })
          );
        }
      } catch (e) { }
      await ch.markRead();
      try {
        setUnreadChat(false);
      } catch (e) { }
    } catch (e) { }
  }, [activeChannel, channel]);

  useEffect(() => {
    const node = chatMainRef.current;
    if (!node) return;
    const onFocusIn = (e) => {
      try {
        const target = e.target;
        if (
          target &&
          target.classList &&
          target.classList.contains("str-chat__textarea__textarea")
        ) {
          markChannelRead();
        }
      } catch (e2) { }
    };
    node.addEventListener("focusin", onFocusIn);
    return () => {
      try {
        node.removeEventListener("focusin", onFocusIn);
      } catch (e) { }
    };
  }, [markChannelRead]);

  // Function to fetch chat history
  const fetchChatHistory = async (limit = 100) => {
    if (!channel) {
      console.log("❌ Channel not available");
      return null;
    }

    try {
      console.log("🔵 Fetching chat history...");

      // Query messages from channel
      const response = await channel.query({
        messages: { limit: limit },
        watch: false,
      });

      const messages = response.messages || [];

      console.log("✅ Chat History Retrieved:", {
        totalMessages: messages.length,
        channelId: channel.id,
        channelName: channel.data.name,
      });

      // Format chat history
      const formattedHistory = messages.map((msg) => ({
        id: msg.id,
        text: msg.text,
        user: {
          id: msg.user.id,
          name: msg.user.name,
        },
        createdAt: msg.created_at,
        attachments: msg.attachments,
        reactions: msg.reaction_counts,
        replyCount: msg.reply_count,
        pinned: msg.pinned,
      }));

      console.log("📜 Formatted Chat History:", formattedHistory);

      return formattedHistory;
    } catch (error) {
      console.error("❌ Error fetching chat history:", error);
      return null;
    }
  };

  // Export full chat history
  const exportChatHistory = async () => {
    try {
      const ch = activeChannel || channel;

      if (!chatClient || !ch) {
        console.log("Chat client or channel not available for export");
        return;
      }

      setIsExporting(true);

      const channelType = ch.type || "livestream";
      const channelId = ch.id;

      const messages = await fetchChannelMessagesForExport(
        channelType,
        channelId,
        200
      );

      if (!messages || messages.length === 0) {
        try {
          toast?.info?.("No chat messages to export for this conversation.");
        } catch (e) { }
        return;
      }

      const meetingId = meetingSession

      const roomName = meetingSettings?.roomName || "N/A";
      const meetingName =
        customMeetingName || meetingSettings?.meetingName || "N/A";

      const headerLines = [
        `Chat transcript for meeting room: ${roomName}`,
        `Meeting name: ${meetingName}`,
        `Generated at: ${new Date().toLocaleString()}`,
        "",
      ];

      const transcriptBody = formatMessagesAsTranscript(messages);
      const content = `${headerLines.join("\n")}\n${transcriptBody}`;

      const blob = new Blob([content], {
        type: "text/plain;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const fileNameBase =
        roomName !== "N/A"
          ? roomName
          : meetingName !== "N/A"
            ? meetingName
            : String(meetingId);
      const safeFileName = fileNameBase.replace(/[^\w\-]+/g, "_");
      link.href = url;
      link.download = `${safeFileName}-chat-${new Date()
        .toISOString()
        .slice(0, 10)}.txt`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting chat history:", error);
      try {
        toast?.error?.("Failed to export chat. Please try again.");
      } catch (e) { }
    } finally {
      setIsExporting(false);
    }
  };

  const createPrivateChannel = async (participantId, participantName) => {
    if (!chatClient || !participantId) {
      console.log("❌ Chat client or participant ID not available");
      return null;
    }

    try {
      const currentUserId = mMeeting.localParticipant.id;
      const channelId = [currentUserId, participantId].sort().join("-");

      if (privateChannels.has(channelId)) {
        const existing = privateChannels.get(channelId);
        try {
          await existing.watch();
        } catch (e) { }
        if (!subscribedPrivateChannelsRef.current.has(channelId)) {
          existing.on("message.new", (event) => {
            const msg = event?.message;
            if (!msg) return;
            const senderName = msg.user?.name || msg.user?.id || "User";
            const otherMemberId = [currentUserId, participantId].find(
              (id) => id !== msg.user?.id
            );
            const recipientName =
              otherMemberId === participantId
                ? participantName
                : mMeeting.localParticipant.displayName || "You";
            setPrivateFeed((prev) => [
              ...prev,
              {
                id: msg.id,
                text: msg.text,
                createdAt: msg.created_at,
                senderName,
                recipientName,
              },
            ]);
          });
          subscribedPrivateChannelsRef.current.add(channelId);
        }
        return existing;
      }

      const privateChannel = chatClient.channel("messaging", channelId, {
        members: [currentUserId, participantId],
        name: `Private: ${mMeeting.localParticipant.displayName} & ${participantName}`,
        reactions: true,
        replies: true,
        uploads: true,
        typing_events: true,
        read_events: true,
        connect_events: true,
        max_message_length: 5000,
      });

      await privateChannel.create();
      try {
        await privateChannel.watch();
      } catch (e) { }
      if (!subscribedPrivateChannelsRef.current.has(channelId)) {
        privateChannel.on("message.new", (event) => {
          const msg = event?.message;
          if (!msg) return;
          const senderName = msg.user?.name || msg.user?.id || "User";
          const otherMemberId = [currentUserId, participantId].find(
            (id) => id !== msg.user?.id
          );
          const recipientName =
            otherMemberId === participantId
              ? participantName
              : mMeeting.localParticipant.displayName || "You";
          setPrivateFeed((prev) => [
            ...prev,
            {
              id: msg.id,
              text: msg.text,
              createdAt: msg.created_at,
              senderName,
              recipientName,
            },
          ]);
        });
        subscribedPrivateChannelsRef.current.add(channelId);
      }

      setPrivateChannels((prev) =>
        new Map(prev).set(channelId, privateChannel)
      );

      return privateChannel;
    } catch (error) {
      console.error(" Error creating private channel:", error);
      return null;
    }
  };

  const sendPrivateMessage = async (
    participantId,
    participantName,
    messageText
  ) => {
    try {
      const privateChannel = await createPrivateChannel(
        participantId,
        participantName
      );
      if (privateChannel) {
        await privateChannel.sendMessage({
          text: messageText,
          user_id: mMeeting.localParticipant.id,
        });
        setPrivateFeed((prev) => [
          ...prev,
          {
            id: `${privateChannel.id}-${Date.now()}`,
            text: messageText,
            createdAt: new Date().toISOString(),
            senderName: mMeeting.localParticipant.displayName || "You",
            recipientName: participantName,
          },
        ]);
        return true;
      }
      return false;
    } catch (error) {
      console.error(" Error sending private message:", error);
      return false;
    }
  };

  useEffect(() => {
    if (channel && typeof window !== "undefined") {
      window.getChatHistory = fetchChatHistory;
      window.sendPrivateMessage = sendPrivateMessage;
      window.exportChatHistory = exportChatHistory;
    }
  }, [channel, exportChatHistory]);

  useEffect(() => {
    let mounted = true;
    let currentChannel = null;

    const initChat = async () => {
      if (!mMeeting?.localParticipant) {
        return;
      }

      try {
        // Get participant info
        const userId = mMeeting.localParticipant.id;
        const userName = mMeeting.localParticipant.displayName || "Guest";
        const baseMeetingId = meetingSession;
        console.log("meetingId", mMeeting);

        // Initialize GetStream chat client with user info
        const client = await initGetStreamChat(userId, userName);

        if (client && mounted) {
          setChatClient(client);

          // Determine channel ID based on breakout room status
          let channelId = baseMeetingId;
          if (currentRoom && rooms[currentRoom]) {
            // If in a breakout room, create channel ID with room name and session ID
            const roomName = rooms[currentRoom].name || currentRoom;
            // Sanitize room name for use in channel ID (remove special chars, spaces)
            const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
            channelId = `${baseMeetingId}-${sanitizedRoomName}-${currentRoom}`;
            console.log("🔵 Creating breakout room chat channel:", channelId);
          } else {
            console.log("🔵 Creating main meeting chat channel:", channelId);
          }

          // Get or create channel using 'livestream' type which has open read permissions
          // This allows all authenticated users to read/write messages
          const chatChannel = client.channel("livestream", channelId);

          // Check if channel already exists
          const channelState = await chatChannel.watch();

          // If channel is newly created, this user is the creator/moderator
          const isNewChannel = !channelState.channel.created_by;
          const isCreator = channelState.channel.created_by?.id === userId;

          console.log("🔵 Channel info:", {
            isNewChannel,
            createdBy: channelState.channel.created_by?.id,
            currentUser: userId,
            userName: userName,
            isCreator,
          });

          // Update channel settings only if it's a new channel
          if (isNewChannel) {
            console.log("🔵 New channel - initializing settings...");
            const channelDisplayName = currentRoom && rooms[currentRoom] 
              ? `Breakout: ${rooms[currentRoom].name}` 
              : `Meeting ${baseMeetingId}`;
            await chatChannel.update({
              name: channelDisplayName,
              // Enable all features
              reactions: true,
              replies: true,
              uploads: true,
              typing_events: true,
              read_events: true,
              connect_events: true,
              max_message_length: 5000,
              url_enrichment: true,
              commands: ["giphy", "flag", "mute", "ban"],
            });

            // First user (channel creator) is automatically moderator
            console.log(
              "✅ MODERATOR ASSIGNED:",
              userName,
              "(Meeting Creator)"
            );
            console.log("   🔹 Can: pin, flag, mute, ban messages/users");
            setIsModerator(true);
            // try {
            //   await chatChannel.addModerators([userId]);
            // } catch (e) {
            //   console.log(
            //     "Failed to add current user as moderator (new channel)",
            //     e
            //   );
            // }
          } else {
            console.log("🔵 Existing channel - joining as regular member");
            console.log(
              "🔵 Channel creator:",
              channelState.channel.created_by?.id
            );

            // Only channel creator has moderator powers
            if (isCreator) {
              console.log(
                "✅ MODERATOR:",
                userName,
                "(You are the meeting creator)"
              );
              console.log("   🔹 Can: pin, flag, mute, ban messages/users");
              setIsModerator(true);
              // try {
              //   await chatChannel.addModerators([userId]);
              // } catch (e) {
              //   console.log(
              //     "Failed to add current user as moderator (existing channel)",
              //     e
              //   );
              // }
            } else {
              console.log("👤 REGULAR USER:", userName);
              console.log("   🔹 Can: send messages, react, reply, quote");
              console.log("   ❌ Cannot: pin, flag, mute, or ban");
              setIsModerator(false);
            }
          }

          // Add current user to members
          // try {
          //   await chatChannel.addMembers([userId]);
          // } catch (error) {
          //   console.log("User already a member or error:", error);
          // }

          if (mounted) {
            currentChannel = chatChannel;
            setChannel(chatChannel);
            setActiveChannel(chatChannel);
          }
        }

        if (mounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error initializing chat:", error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    if (meetingSession) {
      initChat();
    }

    // Cleanup on unmount
    return () => {
      mounted = false;

      const cleanup = async () => {
        try {
          if (currentChannel) {
            await currentChannel.stopWatching();
          }
        } catch (error) {
          console.error("Error during cleanup:", error);
        }
      };

      cleanup();
    };
  }, [mMeeting?.localParticipant?.id, mMeeting?.meetingId, meetingSession, currentRoom, rooms]);

  // Sync new participants joining the VideoSDK meeting with chat
  useEffect(() => {
    if (!channel || !mMeeting || !chatClient) return;

    const syncParticipants = async () => {
      try {
        const allParticipants = Array.from(mMeeting.participants.keys());
        const localId = mMeeting.localParticipant?.id;
        const allMemberIds = [localId, ...allParticipants]
          .filter(Boolean)
          .filter((id, index, self) => self.indexOf(id) === index);

        // // Get current channel members to avoid re-adding
        // const channelMembers = Object.keys(channel.state.members || {});

        // // Filter out members who are already in the channel
        // const newMemberIds = allMemberIds.filter(
        //   (id) => !channelMembers.includes(id)
        // );

        // if (newMemberIds.length > 0) {
        //   // For GetStream, we can only add users who have been created/connected
        //   // Since we can't create users client-side for other participants,
        //   // we'll only track the current user
        //   console.log("New participants detected:", newMemberIds);
        //   console.log("Note: Other participants must join chat themselves");
        // }

        // console.log("Current chat members:", channelMembers);
      } catch (error) {
        console.error("Error syncing participants:", error);
      }
    };

    syncParticipants();
  }, [mMeeting?.participants, channel, chatClient]);

  // Custom Message component that filters by breakout room
  const FilteredMessage = (props) => {
    const { message } = props;
    const senderId = message?.user?.id;
    const currentUserId = mMeeting?.localParticipant?.id;
    
    // Filter messages by breakout room - only show messages from participants in the same room
    if (currentRoom && senderId && senderId !== currentUserId) {
      // If in a breakout room, only show messages from participants in the same room
      if (!isInSameRoom(senderId)) {
        return null; // Don't render messages from participants in different rooms
      }
    } else if (!currentRoom && senderId && senderId !== currentUserId) {
      // If in main meeting, only show messages from participants not in any room
      if (participantRooms[senderId]) {
        return null; // Don't render messages from participants in breakout rooms
      }
    }
    
    // Render the message if it passes the filter
    return <CustomMessage {...props} />;
  };

  useEffect(() => {
    const ch = activeChannel || channel;
    if (!chatClient || !ch) return;

    const collectPinned = async () => {
      try {
        const msgs = Array.isArray(ch?.state?.messages)
          ? ch.state.messages
          : [];
        
        // Filter messages by breakout room before processing pinned messages
        const filteredMsgs = msgs.filter((m) => {
          const senderId = m?.user?.id;
          const currentUserId = mMeeting?.localParticipant?.id;
          
          if (!senderId || senderId === currentUserId) return true;
          
          // Filter by breakout room
          if (currentRoom) {
            // If in a breakout room, only include messages from participants in the same room
            return isInSameRoom(senderId);
          } else {
            // If in main meeting, only include messages from participants not in any room
            return !participantRooms[senderId];
          }
        });
        
        const ordered = filteredMsgs
          .filter((m) => m?.pinned)
          .sort((a, b) => {
            const ta = new Date(
              a?.pinned_at || a?.updated_at || a?.created_at || 0
            ).getTime();
            const tb = new Date(
              b?.pinned_at || b?.updated_at || b?.created_at || 0
            ).getTime();
            return tb - ta;
          });

        // Set all pinned messages instead of just the latest
        setPinnedMessages(ordered);

        // Update local state for all pinned messages
        ordered.forEach((msg) => {
          updateLocalPinnedState(msg, true, ch);
        });
      } catch (e) {
        // ignore
      }
    };

    collectPinned();
    const off1 = ch.on("message.updated", collectPinned);
    const off2 = ch.on("message.new", collectPinned);
    const off3 = ch.on("message.deleted", collectPinned);
    const off4 = ch.on("channel.updated", collectPinned);
    return () => {
      try {
        off1?.();
        off2?.();
        off3?.();
        off4?.();
      } catch (e) { }
    };
  }, [chatClient, activeChannel, channel, currentRoom, isInSameRoom, participantRooms, mMeeting?.localParticipant?.id]);

  useEffect(() => {
    if (!chatClient) return;
    const unsubscribe = chatClient.on("message.new", async (event) => {
      try {
        const cid = event?.cid || "";
        const type = event?.channel?.type || cid.split(":")[0];
        if (type !== "messaging") return;
        const msg = event?.message;
        if (!msg) return;

        const currentUserId = mMeeting?.localParticipant?.id;
        if (!currentUserId) return;

        const senderId = msg.user?.id;
        
        // Filter private messages by breakout room - only show messages from participants in the same room
        if (senderId && senderId !== currentUserId) {
          if (currentRoom) {
            // If in a breakout room, only process messages from participants in the same room
            if (!isInSameRoom(senderId)) {
              console.log("🔴 Private chat message filtered out - sender not in same breakout room:", senderId);
              return; // Ignore messages from participants in different rooms
            }
          } else {
            // If in main meeting, only process messages from participants NOT in any breakout room
            if (participantRooms[senderId]) {
              console.log("🔴 Private chat message filtered out - sender is in a breakout room:", senderId);
              return; // Ignore messages from participants in breakout rooms
            }
          }
        }

        let dmChannel = event?.channel;
        if (!dmChannel && cid.includes(":")) {
          const [, id] = cid.split(":");
          dmChannel = chatClient.channel("messaging", id);
          try {
            await dmChannel.watch();
          } catch (e) { }
        }
        if (!dmChannel) return;

        const memberIds = Object.keys(dmChannel?.state?.members || {});
        if (!memberIds.includes(currentUserId)) return;

        const senderName = msg.user?.name || msg.user?.id || "User";
        const otherMemberId =
          memberIds.find((id) => id !== msg.user?.id) || currentUserId;
        const recipientName =
          otherMemberId === currentUserId
            ? mMeeting?.localParticipant?.displayName || "You"
            : dmChannel?.state?.members?.[otherMemberId]?.user?.name || "User";

        setPrivateFeed((prev) => [
          ...prev,
          {
            id: msg.id,
            text: msg.text,
            createdAt: msg.created_at,
            senderName,
            recipientName,
          },
        ]);
      } catch (e) {
        // ignore
      }
    });
    return () => {
      try {
        unsubscribe?.();
      } catch (e) { }
    };
  }, [chatClient, mMeeting?.localParticipant?.id, currentRoom, isInSameRoom]);

  useEffect(() => {
    if (!chatClient) return;
    const offUpdated = chatClient.on("message.updated", () => {
      setMessageVersion((v) => v + 1);
    });
    const offDeleted = chatClient.on("message.deleted", () => {
      setMessageVersion((v) => v + 1);
    });
    return () => {
      try {
        offUpdated?.();
        offDeleted?.();
      } catch (e) { }
    };
  }, [chatClient]);

  useEffect(() => {
    if (!chatClient || !channel) return;
    const applySelection = async () => {
      if (selectedRecipient === "everyone") {
        setActiveChannel(channel);
      } else {
        const participant = Array.from(
          mMeeting?.participants?.values?.() || []
        ).find((p) => p.id === selectedRecipient);
        if (participant) {
          const name = participant.displayName || "Guest";
          const priv = await createPrivateChannel(participant.id, name);
          if (priv) setActiveChannel(priv);
        }
      }
    };
    applySelection();
  }, [selectedRecipient, chatClient, channel, mMeeting?.participants]);
  
  // Re-initialize chat when breakout room changes to switch channels
  useEffect(() => {
    if (!chatClient) return;
    
    // When breakout room changes, we need to reinitialize the chat
    // The channel will be recreated in the main initChat effect
    // For now, we'll just trigger a re-render by updating message version
    setMessageVersion(v => v + 1);
  }, [currentRoom, chatClient]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center text-white bg-black"
        style={{ height: panelHeight }}
      >
        <p>Loading chat...</p>
      </div>
    );
  }

  if (!chatClient || !channel) {
    return (
      <div
        className="flex flex-col items-center justify-center text-white bg-black"
        style={{ height: panelHeight, padding: "20px" }}
      >
        <p style={{ marginBottom: "10px", fontSize: "16px" }}>
          Unable to load chat
        </p>
        <p style={{ fontSize: "12px", color: "#999", textAlign: "center" }}>
          Check browser console (F12) for details
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "15px",
            padding: "8px 16px",
            backgroundColor: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  const getParticipants = () => {
    if (!mMeeting?.participants) return [];

    const participants = Array.from(mMeeting.participants.values()).map(
      (participant) => ({
        id: participant.id,
        name: participant.displayName || "Guest",
        isLocal: participant.id === mMeeting.localParticipant?.id,
      })
    );

    // Filter by breakout room - only show participants in the same room (for chat selection)
    let filtered = participants.filter((p) => !p.isLocal); // Exclude current user
    
    if (currentRoom) {
      // If in a breakout room, only show participants in the same room for chat
      filtered = filtered.filter((p) => isInSameRoom(p.id));
    } else {
      // If in main meeting, only show participants not in any room
      filtered = filtered.filter((p) => !participantRooms[p.id]);
    }

    return filtered;
  };

  const handleMessageSend = async (messageData) => {
    if (selectedRecipient === "everyone") {
      return true;
    } else {
      const participant = getParticipants().find(
        (p) => p.id === selectedRecipient
      );
      if (participant) {
        const success = await sendPrivateMessage(
          participant.id,
          participant.name,
          messageData.text
        );

        if (success) {
          return false;
        } else {
          console.error(" Failed to send private message");
          return false;
        }
      }
    }

    return true;
  };

  return (
    <div
      style={{
        height: panelHeight,
        backgroundImage: `url(${chatBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      className="custom-chat-panel w-full"
    >
      <Chat client={chatClient} theme="str-chat__theme-dark">
        <div className="chat-container">
          <Channel
            channel={activeChannel || channel}
            CustomMessageActionsList={ModerationActions}
          >
            <Window>
              <div className="chat-main" tabIndex={0} ref={chatMainRef}>
                {pinnedMessages.length > 0 && (
                  <div
                    className={`pinned-messages-container ${isMobileLandscape ? "pb-2" : "pb-4"
                      }`}
                  >
                    <div className="pinned-label-wrapper">
                      <div
                        className={`pinned-label ${isMobileLandscape ? "text-sm" : "text-base"
                          }`}
                      >
                        PINNED MESSAGE
                      </div>
                    </div>
                    {pinnedMessages.map((pm) => {
                      const userName = pm?.user?.name || pm?.user?.id || "User";
                      const userInitials = getUserInitials(userName);
                      const messageTime = pm?.created_at
                        ? new Date(pm.created_at)
                          .toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                          .toLowerCase()
                        : pm?.pinned_at
                          ? new Date(pm.pinned_at)
                            .toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                            .toLowerCase()
                          : "";

                      return (
                        <div key={pm.id} className="pinned-message-item">
                          <div className="pinned-avatar">
                            {pm?.user?.image ? (
                              <img
                                src={pm.user.image}
                                alt={userName}
                                className="pinned-avatar-image"
                              />
                            ) : (
                              <div className="pinned-avatar-fallback">
                                {userInitials}
                              </div>
                            )}
                          </div>
                          <div className="pinned-message-bubble">
                            <div className="pinned-message-header">
                              <span className="pinned-message-author">
                                {userName}
                              </span>
                              <span className="pinned-message-time">
                                {messageTime}
                              </span>
                            </div>
                            <div className="pinned-message-text">
                              {pm?.text || "[attachment]"}
                            </div>
                          </div>
                          <div className="pinned-message-menu">
                            <div className="pinned-menu-wrapper">
                              <button
                                className="pinned-menu-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenPinnedMenuId(
                                    openPinnedMenuId === pm.id ? null : pm.id
                                  );
                                }}
                                title="More options"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <circle
                                    cx="8"
                                    cy="3"
                                    r="1.5"
                                    fill="currentColor"
                                  />
                                  <circle
                                    cx="8"
                                    cy="8"
                                    r="1.5"
                                    fill="currentColor"
                                  />
                                  <circle
                                    cx="8"
                                    cy="13"
                                    r="1.5"
                                    fill="currentColor"
                                  />
                                </svg>
                              </button>
                              {openPinnedMenuId === pm.id && (
                                <div className="pinned-menu-dropdown">
                                  {isModerator && (
                                    <button
                                      className="pinned-menu-dropdown-item"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setOpenPinnedMenuId(null);
                                        try {
                                          const ch = activeChannel || channel;
                                          await chatClient?.unpinMessage(pm);
                                          setPinnedMessages((prev) =>
                                            prev.filter(
                                              (msg) => msg.id !== pm.id
                                            )
                                          );
                                          updateLocalPinnedState(
                                            { ...pm, pinned: false },
                                            false,
                                            ch
                                          );
                                        } catch (error) {
                                          console.error(
                                            "Failed to unpin message",
                                            error
                                          );
                                        }
                                      }}
                                    >
                                      Unpin
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <MessageList
                  key={`${(activeChannel || channel)?.cid || ""
                    }:${messageVersion}`}
                  TypingIndicator={() => <TypingIndicator />}
                  closeReactionSelectorOnClick={true}
                  Message={FilteredMessage}
                  messageActions={["reply", "react", "quote", "flag", "pin"]}
                />

                {privateFeed.length > 0 && (
                  <div
                    style={{
                      padding: isMobileLandscape ? "4px 8px" : "8px 12px",
                      color: "#ddd",
                      fontSize: isMobileLandscape ? "10px" : "12px",
                    }}
                  >
                    {(() => {
                      const pm = privateFeed[privateFeed.length - 1];
                      const currentUserName =
                        mMeeting.localParticipant?.displayName || "You";
                      const isFromMe =
                        pm.senderName === currentUserName ||
                        pm.senderName === "You";
                      const otherPersonName = isFromMe
                        ? pm.recipientName
                        : pm.senderName;

                      const otherParticipant = getParticipants().find(
                        (p) => p.name === otherPersonName
                      );

                      const isCurrentlyInPrivateChat =
                        selectedRecipient !== "everyone" &&
                        otherParticipant &&
                        selectedRecipient === otherParticipant.id;

                      if (isCurrentlyInPrivateChat) {
                        return null;
                      }

                      return (
                        <div
                          onClick={() => {
                            if (otherParticipant) {
                              setSelectedRecipient(otherParticipant.id);
                            }
                          }}
                          style={{
                            background: "rgba(37, 99, 235, 0.15)",
                            border: "1px solid #2563eb",
                            borderRadius: "6px",
                            padding: isMobileLandscape ? "6px" : "10px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(37, 99, 235, 0.25)";
                            e.currentTarget.style.transform = "scale(1.01)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(37, 99, 235, 0.15)";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                        >
                          <div
                            style={{
                              color: "#93c5fd",
                              fontWeight: "600",
                              fontSize: isMobileLandscape ? "10px" : "12px",
                            }}
                          >
                            🔒 Private message {isFromMe ? "to" : "from"}{" "}
                            <strong>{otherPersonName}</strong>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div>
                  <div
                    style={{
                      background: "#3A3B3B",
                      borderRadius: "24px 24px 0px 0px",
                      padding: isMobileLandscape
                        ? "6px 8px 8px 8px"
                        : "10px 12px 12px 12px",
                      marginBottom: isMobileLandscape ? -8 : -12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: isMobileLandscape ? "4px" : "8px",
                      }}
                    >
                      <div
                        style={{
                          color: "#E5E7EB",
                          fontSize: isMobileLandscape ? 12 : 16,
                          fontWeight: 600,
                        }}
                      >
                        Send To
                      </div>

                      <div className="recipient-pill">
                        <img
                          src="/group.png"
                          alt="User"
                          className="recipient-icon"
                        />
                        <select
                          className="recipient-select"
                          value={selectedRecipient}
                          onChange={(e) => {
                            setSelectedRecipient(e.target.value);
                          }}
                        >
                          <option value="everyone">Everyone</option>
                          {getParticipants().map((participant) => (
                            <option key={participant.id} value={participant.id}>
                              {participant.name}
                            </option>
                          ))}
                        </select>
                        <span className="recipient-caret" />
                      </div>
                    </div>

                    <div
                      style={{
                        height: 1,
                        background: "rgba(229,231,235,0.35)",
                        marginBottom: isMobileLandscape ? 4 : 8,
                      }}
                    />

                    <MessageInput
                      placeholder={
                        selectedRecipient === "everyone"
                          ? "Type a message to everyone..."
                          : (() => {
                            const participant = getParticipants().find(
                              (p) => p.id === selectedRecipient
                            );
                            return participant
                              ? `Type a private message to ${participant.name}...`
                              : "Select recipient first...";
                          })()
                      }
                      disabled={!activeChannel}
                      onFocus={markChannelRead}
                      clearEditingState={() => {
                        try {
                          const composer = (activeChannel || channel)
                            ?.messageComposer;
                          if (composer) {
                            try {
                              composer.restore?.();
                            } catch (e) { }
                            try {
                              composer.setQuotedMessage?.(null);
                            } catch (e) { }
                            try {
                              composer.clear?.();
                            } catch (e) { }
                            try {
                              composer.editedMessage = undefined;
                            } catch (e) { }
                          }
                        } catch (e) { }
                      }}
                    />
                  </div>
                </div>
              </div>
            </Window>
            <Thread />
          </Channel>
        </div>
      </Chat>
    </div>
  );
}
